import type {
  CreateNarrativeFormRequest,
  NarrativeForm,
  UpdateNarrativeFormRequest,
} from '@musedrops/shared';
import { fromDbKeys, toDbKeys, snakeCase } from '@musedrops/shared';
import { supabase } from '../services/supabase';

export interface ListNarrativeFormsOptions {
  page?: number;
  perPage?: number;
  sortField?: keyof NarrativeForm | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listNarrativeForms(
  options: ListNarrativeFormsOptions = {}
): Promise<{ data: NarrativeForm[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase.from('narrative_forms').select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('key', { ascending: true });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch narrative forms: ${error.message}`);
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<NarrativeForm>(row as Record<string, unknown>)
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export async function getNarrativeForm(id: string): Promise<NarrativeForm> {
  const { data, error } = await supabase
    .from('narrative_forms')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch narrative form: ${error.message}`);
  }

  if (!data) {
    throw new Error('Narrative form not found');
  }

  return fromDbKeys<NarrativeForm>(data as Record<string, unknown>);
}

export async function createNarrativeForm(
  formData: CreateNarrativeFormRequest
): Promise<NarrativeForm> {
  const { data, error } = await supabase
    .from('narrative_forms')
    .insert(toDbKeys(formData))
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to create narrative form: ${error.message}`);
  }

  return fromDbKeys<NarrativeForm>(data as Record<string, unknown>);
}

export async function updateNarrativeForm(
  id: string,
  formData: UpdateNarrativeFormRequest
): Promise<NarrativeForm> {
  const { data, error } = await supabase
    .from('narrative_forms')
    .update(toDbKeys(formData))
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to update narrative form: ${error.message}`);
  }

  return fromDbKeys<NarrativeForm>(data as Record<string, unknown>);
}

export async function deleteNarrativeForm(id: string): Promise<void> {
  const { error } = await supabase
    .from('narrative_forms')
    .delete()
    .eq('id', id);

  if (error) {
    throw new Error(`Failed to delete narrative form: ${error.message}`);
  }
}

/**
 * Get all narrative forms as a simple array
 */
export async function listAllNarrativeForms(): Promise<NarrativeForm[]> {
  const { data } = await listNarrativeForms({});
  return data;
}

/**
 * Count stories using a specific narrative form key.
 * Best-effort: the stories.form_key column may not exist in every environment,
 * in which case this resolves to 0 rather than throwing.
 */
export async function countStoriesWithForm(formKey: string): Promise<number> {
  const { count, error } = await supabase
    .from('stories')
    .select('id', { count: 'exact', head: true })
    .eq('form_key', formKey);

  if (error) {
    // form_key column may not exist yet — treat as no usage rather than failing.
    return 0;
  }

  return count ?? 0;
}
